import { Language } from './location';

type OrderItem = {
  id: number;
  quantity: number;
  ticketTypeId: number;
  ticketTypeName: string;
  ticketTypeEnglishName: string;
  standingId?: number;
  seatId?: number;
};

export type BoxOfficeOrderItem = {
  id: number;
  date: string;
  firstName: string;
  lastName: string;
  paymentMethod: string;
  event: {
    id: number;
    name: string;
    nameEnglish: string;
    date: string;
    startingTime: string;
  };
  partnerUser: {
    id: number;
    firstName: string;
    lastName: string;
  };
  orderItems: OrderItem[];
};

export enum PaymentMethod {
  CASH = 'CASH',
  CARD = 'CARD',
  VOUCHER = 'VOUCHER',
  OTP_SZEP_CARD = 'OTP_SZEP_CARD',
  MBH_SZEP_CARD = 'MBH_SZEP_CARD',
  TRANSFER = 'TRANSFER',
  OTHER = 'OTHER',
  ROOM_LOAN = 'ROOM_LOAN',
}

export enum paymentMethodLabelMap {
  CASH = 'cash',
  CARD = 'card',
  VOUCHER = 'voucher',
  OTP_SZEP_CARD = 'otpSzepCard',
  MBH_SZEP_CARD = 'mbhSzepCard',
  TRANSFER = 'transfer',
  OTHER = 'other',
  ROOM_LOAN = 'roomLoan',
}

export enum BillingOptionType {
  NTAK = 'ntak',
  INVOICE = 'invoice',
}

export enum InvoiceType {
  INDIVIDUAL = 'INDIVIDUAL',
  COMPANY = 'COMPANY',
}

export type OrderDetailsWithRelatedData = {
  id: number;
  partnerUserId: number;
  eventId: number;
  language: Language;
  event: {
    name: string;
    nameEnglish: string;
    date: string;
    startingTime: string;
    hasSeatMap: boolean;
  };
  order: {
    partnerUser: {
      firstName: string;
      lastName: string;
    };
    customer: {
      firstName: string;
      lastName: string;
      companyName?: string;
    };
    orderId: number;
    orderDate: string;
    paymentMethod: PaymentMethod;
  };
  billingOptionType: BillingOptionType;
  invoice?: {
    personal: string;
    company: string;
    firstName: string;
    lastName: string;
    companyName?: string;
    zipCode: string;
    city: string;
    address: string;
    email: string;
    taxNumber?: string;
  };
  ntak?: {
    zipCode: string;
  };
};

export type OrderedTicketWithTicketTypeName = {
  id: number;
  barcode: number;
  link: string;
  sent: boolean;
  seatName: string | null;
  rowName: string | null;
  sectionName: string | null;
  sectionNameEnglish: string | null;
  ticketTypeName: string;
  ticketTypeEnglishName: string;
  ticketPrice: number;
};

export type SoldBoxOfficeOfferWithQty = {
  offerId: number;
  ticketPrice: number;
  _sum: {
    quantity: number;
  };
};

export enum OrderHistoryAction {
  ALL = 'all',
  MODIFY_PAYMENT_METHOD = 'modifyPaymentMethod',
  REGENERATE = 'regenerate',
  DELETE = 'delete',
}

export type OrderHistory = {
  id: number;
  partnerUserId: number;
  partnerUser: {
    firstName: string;
    lastName: string;
  };
  action: OrderHistoryAction;
  date: string;
  details: string;
};

export type BoxOfficeSelectedTicket = {
  ticketTypeId: number;
  ticketTypeName?: string;
  ticketTypeNameEnglish?: string;
  offerId: number;
  offerName?: string;
  offerNameEnglish?: string;
  ticketPrice: number;
  quantity: number;
  seatId?: number;
  standingId?: number;
};

export type BoxOfficeOrderData = {
  id: number;
  paymentMethod: PaymentMethod;
  partnerUser: {
    firstName: string;
    lastName: string;
  };
  address: string;
  city: string;
  comment: string;
  companyName: string | null;
  countryIsoCode: string;
  date: string;
  email: string;
  event: {
    id: number;
    name: string;
    nameEnglish: string;
    date: string;
    startingTime: string;
    hasSeatMap: boolean;
  };
  eventId: number;
  firstName: string | null;
  invoiceNumber: string;
  invoiceType: string | null;
  lastName: string | null;
  name: string;
  postalCode: string;
  taxNumber: string;
  tickets: BoxOfficeOrderTicket[];
  usedImmediately: boolean;
  userId: number;
};

export type CreateBoxOfficeResponse = {
  id: number;
  eventId: number;
  userId: number;
  boxOfficeUserId: number;
  countryIsoCode: string;
  paymentMethod: PaymentMethod;
  invoiceType: string | null;
  name: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  postalCode: string;
  city: string;
  address: string;
  taxNumber: string;
  email: string;
  comment: string;
  date: Date;
  invoiceNumber: string;
  usedImmediately: boolean;
  tickets: OrderedTicketWithTicketTypeName[];
  pdfData: string;
};

export type BoxOfficeOrderTicket = {
  id: number;
  name: string;
  nameEnglish: string;
  quantity: number;
  ticketTypeId: number;
};

export type BoxOfficeOrderItemDetail = {
  id: number;
  creationId: number;
  ticketTypeId: number;
  offerId: number;
  quantity: number;
  ticketPrice: number;
  seatId?: string;
  seatMapId?: string;
  standingId?: string;
};

export type BoxOfficeOrderDetails = {
  id: number;
  userId: number;
  eventId: number;
  date: string;
  paymentMethod: PaymentMethod;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  address: string;
  city: string;
  postalCode: string;
  countryIsoCode: string;
  companyName: string | null;
  comment: string;
  taxNumber: string;
  invoiceNumber: string;
  invoiceType: string | null;
  usedImmediately: boolean;
  partnerUser: {
    id: number;
    firstName: string;
    lastName: string;
  };
  event: {
    id: number;
    name: string;
    nameEnglish: string;
    date: string;
    startingTime: string;
    hasSeatMap: boolean;
  };
  boxOfficeItems: BoxOfficeOrderItemDetail[];
};

export type BoxOfficeItemHistory = {
  id: number;
  partnerUserId: number;
  boxOfficeItemId: number;
  action: BoxOfficeItemHistoryActionEnum;
  date: string;
  details: string;
  partnerUser: {
    firstName: string;
    lastName: string;
  };
  boxOfficeItem: {
    id: number;
    quantity: number;
    ticketPrice: number;
    ticketTypeId: number;
  };
};

export enum BoxOfficeItemHistoryActionEnum {
  ALL = 'ALL',
  MODIFY_PAYMENT_METHOD = 'MODIFY_PAYMENT_METHOD',
  REGENERATE = 'REGENERATE',
  DELETE = 'DELETE',
}

export const boxOfficeItemHistoryActionLabelMap = {
  [BoxOfficeItemHistoryActionEnum.MODIFY_PAYMENT_METHOD]: 'modifyPaymentMethod',
  [BoxOfficeItemHistoryActionEnum.REGENERATE]: 'regenerate',
  [BoxOfficeItemHistoryActionEnum.DELETE]: 'delete',
  [BoxOfficeItemHistoryActionEnum.ALL]: 'all',
};

export type BoxOfficeOrderHistory = BoxOfficeItemHistory;

export type DailyClosingUserSale = {
  paymentMethod: PaymentMethod;
  quantity: number;
  amount: number;
};

export type DailyClosingIncome = {
  id: string;
  paymentMethod: PaymentMethod;
  amount: number;
};

export type DailyClosingUser = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  totalSales: number;
  sales: DailyClosingUserSale[];
};

export type DailyClosingData = {
  sales: DailyClosingIncome[];
  totalIncome: number;
  users: DailyClosingUser[];
  total: number;
};

export type DailyClosingResponse = {
  data: DailyClosingData[];
  total: number;
};

export type OfferWithCartData = {
  id: number;
  name?: string;
  nameEnglish?: string;
  salesStartDate: string | null;
  salesEndDate: string | null;
  boxOfficeLimit: number;
  ticketPrice: number;
  selectedQuantity: number;
  soldQuantity: number;
  isActive: boolean;
  isSoldOut: boolean;
  hasSelectedAllTickets: boolean;
};

export type TicketTypeWithCartData = {
  id: number;
  name: string;
  nameEnglish: string;
  offers: Record<number, OfferWithCartData>;
};
